import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { ArrowRight, Brain, Zap, Shield } from "lucide-react";

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "ScribPost",
  url: "https://scribpost.com",
  description: "AI writing tool for creators that matches your personal writing voice",
  foundingDate: "2025",
  sameAs: ["https://twitter.com/scribpost", "https://linkedin.com/company/scribpost"],
};

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About ScribPost — AI Writing Tool for Creators</title>
        <meta name="description" content="ScribPost was built to solve one problem: AI that sounds like a robot. Learn how we built voice-matching AI for creators and professionals." />
        <link rel="canonical" href="https://scribpost.com/about" />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#0D1A2A] text-white">
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0D1A2A]/90 backdrop-blur-md border-b border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-2 font-bold text-xl">
              <span className="text-cyan-400">Scrib</span><span className="text-white">Post</span>
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm text-slate-300">
              <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
              <Link href="/about" className="text-white font-medium">About</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link href="/login" className="text-sm text-slate-300 hover:text-white transition-colors hidden sm:block">
                Log in
              </Link>
              <Link
                href="/start"
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 transition-opacity text-white"
              >
                Start Free
              </Link>
            </div>
          </div>
        </nav>

        <div className="pt-32 pb-20 px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">

            {/* Hero */}
            <div className="mb-16">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                We Built ScribPost Because AI Writing{" "}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Sounded Like AI Writing
                </span>
              </h1>

              <div className="space-y-5 text-slate-300 leading-relaxed">
                <p>
                  In early 2025, every major AI writing tool had the same fundamental flaw: they sounded the same. You'd open Jasper, Copy.ai, or ChatGPT, ask for a LinkedIn post about your startup, and get something like: "I'm thrilled to share that we've achieved significant milestones in our journey toward disrupting the industry." Nobody writes like that. Nobody talks like that. But AI did, constantly.
                </p>
                <p>
                  We built ScribPost because we believed the problem wasn't AI writing — it was AI writing that hadn't learned from <em>you</em>. The breakthrough insight was simple: instead of training a model on generic internet text and hoping it produces human-sounding content, train it on the specific human whose posts you need. Your writing samples. Your patterns. Your voice.
                </p>
                <p>
                  ScribPost's voice engine doesn't start with a template — it starts with you. Feed it three LinkedIn posts you've written, and it builds a model of how you structure sentences, which words you favor, how long your paragraphs tend to be, whether you use rhetorical questions, whether you're deadpan or enthusiastic. Then, when you give it a rough idea, it generates content using your patterns — not a generic writer's patterns.
                </p>
              </div>
            </div>

            {/* Mission */}
            <div className="mb-16 p-8 rounded-2xl border border-cyan-500/20 bg-cyan-500/5">
              <h2 className="text-xl font-bold mb-3 text-cyan-400">Our Mission</h2>
              <p className="text-slate-200 leading-relaxed text-lg">
                Make it possible for every creator, founder, and professional to maintain a consistent, authentic online presence — without sacrificing hours of their week to writing. AI should amplify your voice, not replace it with a generic one.
              </p>
            </div>

            {/* How the voice engine works */}
            <div className="mb-16">
              <h2 className="text-2xl font-bold mb-6">How Our Voice Engine Works</h2>
              <p className="text-slate-300 leading-relaxed mb-8">
                The ScribPost voice engine is a multi-layer analysis system that extracts the signature patterns from your writing. Most AI tools give you style controls like "formal / casual / friendly" — blunt dials that produce the same output for everyone who picks "casual." ScribPost goes deeper.
              </p>
              <div className="grid sm:grid-cols-3 gap-5">
                {[
                  {
                    icon: Brain,
                    title: "Pattern Analysis",
                    desc: "We analyze your vocabulary richness, sentence length distribution, paragraph structure, punctuation habits, and transitional phrasing.",
                  },
                  {
                    icon: Zap,
                    title: "Tone Modeling",
                    desc: "We detect emotional register, humor signals, confidence levels, and the specific rhetorical devices you use most — and those you never use.",
                  },
                  {
                    icon: Shield,
                    title: "Platform Adaptation",
                    desc: "Each platform has its own norms. Your voice profile is adapted — not replaced — to match LinkedIn's professional context or Twitter's brevity constraints.",
                  },
                ].map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="p-5 rounded-xl border border-white/10 bg-white/[0.03]">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/20 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-cyan-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-2 text-sm">{title}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-slate-400 text-sm mt-6">
                Want a deeper technical dive?{" "}
                <Link href="/blog/how-to-write-linkedin-posts-that-sound-like-you" className="text-cyan-400 hover:text-cyan-300 transition-colors">
                  Read our guide on writing LinkedIn posts that sound authentically like you →
                </Link>
              </p>
            </div>

            {/* Team */}
            <div className="mb-16">
              <h2 className="text-2xl font-bold mb-8">The Team</h2>
              <div className="flex flex-col sm:flex-row gap-6 p-6 rounded-2xl border border-white/10 bg-white/[0.03]">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-cyan-500/20 flex items-center justify-center shrink-0 text-2xl">
                  👤
                </div>
                <div>
                  <h3 className="font-bold text-lg text-white mb-0.5">Alex Rivera</h3>
                  <p className="text-cyan-400 text-sm mb-3">Founder & CEO</p>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    Former content strategist and product manager who spent five years helping B2B founders build their LinkedIn presence. Frustrated by how AI writing tools produced the same corporate boilerplate for every client, Alex started ScribPost to solve the voice problem from first principles. ScribPost is the tool Alex always wished existed.
                  </p>
                </div>
              </div>
            </div>

            {/* Internal links */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                href="/pricing"
                className="flex items-center justify-between p-5 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition-colors group"
              >
                <div>
                  <p className="font-semibold text-white mb-1">See Pricing</p>
                  <p className="text-xs text-slate-400">Start free. No credit card.</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
              </Link>
              <Link
                href="/blog"
                className="flex items-center justify-between p-5 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition-colors group"
              >
                <div>
                  <p className="font-semibold text-white mb-1">Read Our Blog</p>
                  <p className="text-xs text-slate-400">LinkedIn tips & AI writing guides</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="border-t border-white/10 py-10 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <Link href="/" className="flex items-center gap-2 font-bold text-lg">
                <span className="text-cyan-400">Scrib</span><span className="text-white">Post</span>
              </Link>
              <nav className="flex flex-wrap justify-center gap-6 text-sm text-slate-400">
                <Link href="/about" className="hover:text-white transition-colors">About</Link>
                <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link href="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
              </nav>
              <p className="text-xs text-slate-600">© 2026 ScribPost. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
